import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { Link } from 'react-router-dom';

import { listRuns, type RunListItem, type RunStatus } from '../api/runs';
import { UploadForm } from '../components/UploadForm';

/**
 * Runs list page (T-052).
 *
 * Responsibilities, ordered by user value:
 *
 *  1. Start a new run — the `<UploadForm />` sits at the top because it is
 *     the operator's most frequent action.
 *  2. Show the history of runs with status, totals, and a link to the
 *     detail page. Statuses use WCAG-AA contrast-safe Tailwind pairs
 *     (100/800) with semantic colouring.
 *  3. Poll while any visible row is still working. Terminal states
 *     (COMPLETE / FAILED) do not warrant refetches; polling stops as soon
 *     as every row in view is terminal. The interval is 2s — chosen to
 *     match the server's worker cadence (T-034) without hammering `/runs`.
 *  4. Paginate. The API echoes back `page` + `pageSize`, so the controls
 *     only need local state for the requested page; the server is the
 *     source of truth for totals.
 *
 * Design notes worth defending:
 *
 *  - Query key is `['runs', page, pageSize]`. The upload form invalidates
 *    by the `['runs']` prefix so it refreshes every cached page variant.
 *  - `keepPreviousData` keeps the last page visible while the next page
 *    fetches, so the Prev/Next buttons do not flash "Loading…" on every
 *    click. This is the "swap in place" pagination UX TanStack Query
 *    recommends.
 *  - `noUncheckedIndexedAccess` is on project-wide; every `items[i]` goes
 *    through an `items.some(...)` or a mapped iteration rather than
 *    bracket-indexing with a raw number.
 */

const PAGE_SIZE = 20;

const NON_TERMINAL_STATUSES = new Set<RunStatus>(['PENDING', 'PARSING', 'RENDERING']);

export function RunsListPage(): ReactElement {
  const [page, setPage] = useState<number>(1);

  const query = useQuery({
    queryKey: ['runs', page, PAGE_SIZE] as const,
    queryFn: () => listRuns({ page, pageSize: PAGE_SIZE }),
    placeholderData: keepPreviousData,
    // Poll only while there is at least one non-terminal row on the current
    // page. Returning `false` tells TanStack Query to stop polling; the
    // query still refetches on invalidation (e.g. after an upload).
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const hasInFlight = data.items.some((row) => NON_TERMINAL_STATUSES.has(row.status));
      return hasInFlight ? 2000 : false;
    },
  });

  const data = query.data;
  const items: RunListItem[] = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageSize = data?.pageSize ?? PAGE_SIZE;
  const totalPages = useMemo(() => {
    if (total === 0) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const onPrev = (): void => {
    setPage((p) => Math.max(1, p - 1));
  };
  const onNext = (): void => {
    setPage((p) => Math.min(totalPages, p + 1));
  };

  const isFirstLoad = query.isLoading && items.length === 0;
  const showEmpty = !isFirstLoad && items.length === 0 && !query.isError;

  return (
    <section aria-label="Runs" className="flex flex-col gap-4">
      <h1 className="text-2xl font-semibold text-slate-900">Runs</h1>

      <UploadForm />

      {query.isError ? (
        <p role="alert" className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-800">
          Failed to load runs. Try refreshing the page.
        </p>
      ) : null}

      <div className="overflow-x-auto rounded border border-slate-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <caption className="sr-only">Recent CDR runs</caption>
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-600">
            <tr>
              <th scope="col" className="px-3 py-2">
                Month
              </th>
              <th scope="col" className="px-3 py-2">
                Status
              </th>
              <th scope="col" className="px-3 py-2">
                Uploaded at
              </th>
              <th scope="col" className="px-3 py-2">
                Customers
              </th>
              <th scope="col" className="px-3 py-2">
                PDFs
              </th>
              <th scope="col" className="px-3 py-2">
                Rows
              </th>
              <th scope="col" className="px-3 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isFirstLoad ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-6 text-center text-slate-500"
                  role="status"
                  aria-busy="true"
                >
                  Loading runs…
                </td>
              </tr>
            ) : null}
            {showEmpty ? (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-slate-500">
                  No runs yet. Upload a CSV above to get started.
                </td>
              </tr>
            ) : null}
            {items.map((row) => (
              <RunRow key={row.id} row={row} />
            ))}
          </tbody>
        </table>
      </div>

      <nav
        aria-label="Pagination"
        className="flex items-center justify-between gap-2 text-sm text-slate-700"
      >
        <span>
          Page {page} of {totalPages}
        </span>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onPrev}
            disabled={page <= 1 || query.isFetching}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={onNext}
            disabled={page >= totalPages || query.isFetching}
            className="rounded border border-slate-300 bg-white px-3 py-1.5 font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </nav>
    </section>
  );
}

interface RunRowProps {
  row: RunListItem;
}

function RunRow({ row }: RunRowProps): ReactElement {
  return (
    <tr className="text-slate-800">
      <td className="px-3 py-2 font-mono text-xs">{row.month ?? '—'}</td>
      <td className="px-3 py-2">
        <StatusBadge status={row.status} />
      </td>
      <td className="px-3 py-2">{formatTimestamp(row.uploadedAt)}</td>
      <td className="px-3 py-2 tabular-nums">{row.customerCount ?? '—'}</td>
      <td className="px-3 py-2 tabular-nums">{row.pdfCount ?? '—'}</td>
      <td className="px-3 py-2 tabular-nums">{row.rowCount ?? '—'}</td>
      <td className="px-3 py-2">
        <Link
          to={`/runs/${encodeURIComponent(row.id)}`}
          className="text-brand-teal underline-offset-2 hover:underline"
        >
          View
        </Link>
      </td>
    </tr>
  );
}

interface StatusBadgeProps {
  status: RunStatus;
}

/**
 * Status pill. Tailwind 100/800 colour pairs clear WCAG AA contrast
 * (≥ 4.5:1 for small text) on a white row background.
 */
function StatusBadge({ status }: StatusBadgeProps): ReactElement {
  const cls =
    status === 'COMPLETE'
      ? 'bg-green-100 text-green-800'
      : status === 'FAILED'
        ? 'bg-red-100 text-red-800'
        : 'bg-yellow-100 text-yellow-800';
  return (
    <span
      className={`inline-flex rounded px-2 py-0.5 text-xs font-medium ${cls}`}
      data-testid="run-status-badge"
    >
      {status}
    </span>
  );
}

/**
 * Render an ISO timestamp as a compact, locale-aware string. We intentionally
 * use `Intl.DateTimeFormat` with `en-GB` for deterministic DD/MM/YYYY output
 * in tests and local dev; browsers in other locales see the same display to
 * match the operator-facing UI convention in `docs/architecture.md`.
 */
function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}
